import { DateRangeField, Label, MultiSelect, RadioGroupButtonItem, RadioGroupButtons } from "@tellescope/ui";
import { type ObservationType } from "@tellescope/utils";
import { type Dispatch, type SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import { VitalsChart } from "./VitalsChart/VitalsChart";
import { VitalsSectionItem } from "./VitalsSectionItem";
import { BlockPaginatedList } from "../BlockPaginatedList";
import { useVitalsProgramSubsection } from "./useVitalsProgramSubsection";

export interface VitalsProgramSubsectionProps {
  enduserId: string;
  observationType: ObservationType;
  unit?: string;
  observationId: string | undefined;
  setObservationId: Dispatch<SetStateAction<string | undefined>>;
  chartHeight?: string;
  canUpdateObservations: boolean;
}

export const VitalsProgramSubsection = ({
  enduserId,
  observationType,
  unit,
  observationId,
  setObservationId,
  chartHeight,
  canUpdateObservations,
}: VitalsProgramSubsectionProps) => {
  const { t } = useTranslation("vitals");
  const { filtersForm, deviceOptions, filteredObservations, selectedThresholdIds, vitalsChartProps, fetchNextPage } =
    useVitalsProgramSubsection({ enduserId, observationType, unit, observationId, setObservationId });

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <div className="px-4">
        <div className="border border-gray-200 rounded-lg bg-gray-50/50 p-3 mb-3 flex items-center gap-4">
          <span className="flex items-center gap-2">
            <Label className="text-muted-foreground font-normal text-xs">{t("Date Range:")}</Label>
            <DateRangeField {...filtersForm.getCustomFieldProps("dateRange")} placeholder={t("Pick dates")} size="xs" />
          </span>
          {observationType === "blood-pressure" && (
            <span className="flex items-center gap-2">
              <Label className="text-muted-foreground font-normal text-xs">{t("Meds:")}</Label>
              <RadioGroupButtons {...filtersForm.getCustomFieldProps("meds")} className="flex items-center gap-0">
                <RadioGroupButtonItem value="all" className="px-2 text-xs h-6 font-normal">
                  {t("All")}
                </RadioGroupButtonItem>
                <RadioGroupButtonItem value="before" className="px-2 text-xs h-6 font-normal">
                  {t("Before")}
                </RadioGroupButtonItem>
                <RadioGroupButtonItem value="after" className="px-2 text-xs h-6 font-normal">
                  {t("After")}
                </RadioGroupButtonItem>
              </RadioGroupButtons>
            </span>
          )}
          {observationType === "glucose" && (
            <span className="flex items-center gap-2">
              <Label className="text-muted-foreground font-normal text-xs">{t("Meal:")}</Label>
              <RadioGroupButtons {...filtersForm.getCustomFieldProps("meal")} className="flex items-center gap-1">
                <RadioGroupButtonItem value="all" className="px-2 text-xs h-6">
                  {t("All")}
                </RadioGroupButtonItem>
                <RadioGroupButtonItem value="before" className="px-2 text-xs h-6">
                  {t("Before")}
                </RadioGroupButtonItem>
                <RadioGroupButtonItem value="after" className="px-2 text-xs h-6">
                  {t("After")}
                </RadioGroupButtonItem>
              </RadioGroupButtons>
            </span>
          )}
          <span className="flex items-center gap-2">
            <Label className="text-muted-foreground font-normal text-xs">{t("Device:")}</Label>
            <MultiSelect
              {...filtersForm.getCustomFieldProps("devices")}
              options={deviceOptions}
              className="h-6 min-h-0 text-xs py-0 px-2"
              placeholder={t("All devices")}
              minPopoverWidth={200}
              showAsBadges={false}
            />
          </span>
        </div>
      </div>
      <VitalsChart {...vitalsChartProps} className="border-b-gray-200 border-b pb-2 px-4" height={chartHeight} />
      <BlockPaginatedList className="w-full p-4" onBottomReached={() => fetchNextPage()}>
        {filteredObservations?.map((observation) => (
          <VitalsSectionItem
            key={observation.id}
            observation={observation}
            className="w-full mb-2"
            onClick={() => setObservationId(observation.id === observationId ? undefined : observation.id)}
            selectedThresholdIds={selectedThresholdIds}
            expanded={observation.id === observationId}
            canUpdateObservations={canUpdateObservations}
          />
        ))}
      </BlockPaginatedList>
    </div>
  );
};
